export interface Poll {
  eventId: string
  pollId: string
  status?: POLL_STATUS
  question: string
  responses: string[]
  dateCreated: string
  dateTimePushed?: DateTimeParts
}

export interface EventPollFormat {
  eventId: string
  pollId: string
  question: string
  options: {
    key: string
    value: string
  }[]
}

export interface DateTimeParts {
  month: string
  day: number
  ordinal: string
  time: string
}

export interface PollResult {
  pollId: string
  question: string
  totalRespondents: number
  totalAttendees: number
  answers: {
    label: string
    count: number
    bgColor: string
  }[]
}

export enum POLL_STATUS {
  CREATED = 'CREATED',
  PUSHED = 'PUSHED',
  COMPLETED = 'COMPLETED',
}

//Mock Data
export const mockPollData: Poll[] = [
  {
    eventId: 'eventSample',
    pollId: '1',
    question: 'What brings you joy?',
    dateCreated: '2025-04-17',
    responses: [
      '{"04ce3676-2c94-4ce4-a3fd-bd23e80a1a00":"212","c9655cbe-7a69-4d63-b884-0d507866cd07":"6311"}',
    ],
  },
  {
    eventId: 'eventSample',
    pollId: '2',
    question: "What's your favorite book?",
    dateCreated: '2025-04-15',
    responses: [
      '{"04ce3676-2c94-4ce4-a3fd-bd23e80a1a00":"212","c9655cbe-7a69-4d63-b884-0d507866cd07":"6311"}',
    ],
  },
  {
    eventId: 'eventSample',
    pollId: '3',
    question: "What's your favorite season?",
    dateCreated: '2025-04-15',
    responses: [
      '{"04ce3676-2c94-4ce4-a3fd-bd23e80a1a00":"212","c9655cbe-7a69-4d63-b884-0d507866cd07":"6311"}',
    ],
  },
]

//Transformation functions
export const transformResponseToPoll = (response: any): Poll[] => {
  const transformedData = response.map((item: any) => ({
    eventId: item.eventId,
    pollId: item.uniqueLiveEventEntityId,
    question: item.question,
    responses: item.responses,
    dateCreated: new Date(
      item.creationDateTimeStampSeconds * 1000
    ).toLocaleDateString(), // * 1000 is needed to convert seconds to milliseconds, which is needed for Date constructor
    status: stringToPollStatus(item.status),
    dateTimePushed: item.pushedDateTimeStampSeconds
      ? formatDateTime(item.pushedDateTimeStampSeconds)
      : undefined,
  }))
  return transformedData
}

export const transformPollObjToUserPollFormat = (poll: Poll) => {
  const pairs = poll.responses.map((response) => {
    const [key, value] = Object.entries(JSON.parse(response))[0]
    return { key: key as string, value: value as string }
  })

  return {
    eventId: poll.eventId,
    pollId: poll.pollId,
    question: poll.question,
    options: pairs,
  }
}

// Helper functions
const formatDateTime = (timestamp: number) => {
  const date = new Date(timestamp * 1000) // * 1000 is needed to convert seconds to milliseconds, which is needed for Date constructor
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  const day = date.getDate()
  const ordinal = getOrdinalSuffix(day)
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return { month, day, ordinal, time }
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

const stringToPollStatus = (str: string): POLL_STATUS | undefined => {
  const status = Object.values(POLL_STATUS)
  if (status.includes(str as POLL_STATUS)) {
    return str as POLL_STATUS
  }
  return undefined
}

const POLL_COLORS = [
  '#1BE1F233',
  '#C7B9FF4D',
  '#DEFF4D99',
  '#FF4D5F26',
  '#0C62FB26',
  '#C8A2C8',
]

export const transformPollResultsResponse = (response: any[]): PollResult[] => {
  const flatResponse = response.flat()
  return flatResponse.map((item: any) => ({
    pollId: item.pollId,
    question: item.question,
    totalRespondents: item.totalVotes,
    totalAttendees: item.totalCheckedInAttendees,
    answers: (item.responseOptions || [])
      .sort((a: any, b: any) => b.votes - a.votes)
      .map((option: any, index: number) => ({
        label: option.option,
        count: option.votes,
        bgColor: POLL_COLORS[index % POLL_COLORS.length],
      })),
  }))
}
